// src/components/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Ball, { BallsRow } from "../ui/Ball";
import Countdown, { NEXT_MEGA_MILLIONS, NEXT_POWERBALL } from "../ui/Countdown";
import Icon from "../ui/Icon";
import Spinner from "../ui/Spinner";

import axiosInstance from "../../utils/AxiosInstance";
import { logoutUser } from "../../redux/actions/userActions";
import { LOTTERY, lineToNumbers } from "../../utils/lottery";
import {
  getAllGeneratedNumbers,
  getTotalUserWinnings,
  getTotalUserWinningsByLottery,
  getMyHitDistribution,
  getMyExtraBallImpact,
  getMyBestResult,
  getMyTransactions,
  formatUsd,
  formatDrawDate,
  getDrawStatus,
  getNextDraws,
  formatJackpot,
} from "../../services/resultsService";

/**
 * Authenticated dashboard.
 *
 * Data flow (post backend match-result overlay):
 *   /site/user/logged-user                                       — user + credits
 *   /site/lotto-analyzer/list-numbers-generated?typeLottery=…    — every game
 *      ↑ each row in the response now carries its own
 *        prizeValueUsd / hits / winner / extraMatched, populated
 *        by LottoAnalyzerService.overlayMatchResults().
 *   /site/match-result/total-by-user/{userId}                    — grand-total
 *   /site/match-result/total-by-user-lottery/{userId}/{type}     — per-lottery
 *
 * No per-buy-draw fetch needed any more — the prize travels with the
 * pick. This used to be an N+1 lookup on the dashboard.
 */
export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [user, setUser]           = useState(null);
  const [credits, setCredits]     = useState(0);
  const [totalWon, setTotalWon]   = useState(null);
  const [totalByLottery, setTotalByLottery] = useState({
    MEGA_MILLIONS: null,
    POWER_BALL: null,
  });
  const [picks, setPicks]         = useState({
    MEGA_MILLIONS: [],
    POWER_BALL: [],
  });
  const [loading, setLoading]     = useState(true);
  const [totalSpent, setTotalSpent]           = useState(null);
  const [hitDistribution, setHitDistribution] = useState([]);
  const [extraBallImpact, setExtraBallImpact] = useState([]);
  const [bestResult, setBestResult]           = useState(null);
  const [statsLoading, setStatsLoading]       = useState(true);

  // Authoritative next-draw date + jackpot from the backend (public
  // endpoint). Falls back to client-side estimates / static values below.
  const [nextDraws, setNextDraws]  = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getNextDraws();
      if (!cancelled) setNextDraws(data);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, mmGames, pbGames] = await Promise.all([
          axiosInstance.get("/site/user/logged-user").catch(() => null),
          getAllGeneratedNumbers(LOTTERY.MEGA_MILLIONS).catch(() => []),
          getAllGeneratedNumbers(LOTTERY.POWER_BALL).catch(() => []),
        ]);
        if (cancelled) return;

        const userId = meRes?.data?.id;
        if (meRes?.data) {
          setUser(meRes.data);
          setCredits(meRes.data?.account?.credit ?? 0);
        }
        if (userId) {
          Promise.all([
            getTotalUserWinnings(userId).catch(() => null),
            getTotalUserWinningsByLottery(userId, LOTTERY.MEGA_MILLIONS).catch(() => null),
            getTotalUserWinningsByLottery(userId, LOTTERY.POWER_BALL).catch(() => null),
          ]).then(([grand, mm, pb]) => {
            if (cancelled) return;
            setTotalWon(grand);
            setTotalByLottery({ MEGA_MILLIONS: mm, POWER_BALL: pb });
          });
        }

        const sortByDrawDesc = (arr) =>
          [...arr].sort(
            (a, b) =>
              new Date(b?.buyDraw?.draw?.date || 0).getTime() -
              new Date(a?.buyDraw?.draw?.date || 0).getTime()
          );
        setPicks({
          MEGA_MILLIONS: sortByDrawDesc(mmGames),
          POWER_BALL:    sortByDrawDesc(pbGames),
        });
      } catch {
        /* ProtectedRoute handles 401 */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Personal performance stats — independent of the picks/winnings
  // fetch above so a slow one doesn't hold up the other.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [txs, hits, extra, best] = await Promise.all([
        getMyTransactions().catch(() => []),
        getMyHitDistribution(),
        getMyExtraBallImpact(),
        getMyBestResult(),
      ]);
      if (cancelled) return;
      const spent = txs
        .filter((t) => t.status === "PAID_OUT")
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
      setTotalSpent(spent);
      setHitDistribution(hits);
      setExtraBallImpact(extra);
      setBestResult(best);
      setStatsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    window.dispatchEvent(new CustomEvent("auth:changed", {
      detail: { authenticated: false, source: "dashboard-logout" },
    }));
    navigate("/", { replace: true });
  };

  const flatPicks = useMemo(
    () => [
      ...picks.MEGA_MILLIONS.map((g) => ({ ...g, _type: LOTTERY.MEGA_MILLIONS })),
      ...picks.POWER_BALL.map((g)    => ({ ...g, _type: LOTTERY.POWER_BALL })),
    ],
    [picks]
  );

  const totalPicksThisMonth = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(1);
    cutoff.setHours(0, 0, 0, 0);
    return flatPicks.filter((p) => {
      const d = p?.buyDraw?.draw?.date;
      if (!d) return false;
      return new Date(d).getTime() >= cutoff.getTime();
    }).length;
  }, [flatPicks]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "there";

  return (
    <AppShell>
      <div className="px-4 sm:px-6 py-6">

        <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-semibold mb-1" style={{ fontSize: 24, color: "var(--txt)" }}>
              {greeting}, {firstName}.
            </h1>
            <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
              {new Date().toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/plans")}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-semibold transition-all hover:-translate-y-[1px]"
              style={{
                background: credits > 0 ? "var(--gold)" : "var(--gold-dim)",
                color: credits > 0 ? "#0E2A18" : "var(--gold)",
                border: `1px solid ${credits > 0 ? "var(--gold)" : "var(--gold-border)"}`,
                fontSize: 13,
                boxShadow: credits > 0 ? "0 4px 14px rgba(46,229,107,0.25)" : "none",
                cursor: "pointer",
              }}
              title="Manage credits"
            >
              <Icon name="coin" size={14} />
              {credits} credit{credits === 1 ? "" : "s"}
              <Icon name="arrow-right" size={12} />
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="text-[12px] hover:underline px-2 py-1"
              style={{ color: "var(--txt-2)" }}
            >
              Log out
            </button>
          </div>
        </header>

        {!loading && flatPicks.length === 0 && (
          <Card variant="gold" className="mb-4">
            <div className="flex items-center gap-3">
              <Icon name="sparkles" size={28} style={{ color: "var(--gold)" }} />
              <div>
                <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
                  Welcome aboard.
                </div>
                <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                  Generate your first pick — it will appear here right away,
                  with the prize once the drawing has been processed.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card variant="soft">
            <div className="lg-label mb-1.5">Total winnings</div>
            <div className="font-semibold" style={{
              fontSize: 26,
              color: totalWon !== null && totalWon > 0 ? "var(--gold)" : "var(--txt-3)",
            }}>
              {totalWon !== null ? formatUsd(totalWon) : "—"}
            </div>
            {(totalByLottery.MEGA_MILLIONS !== null || totalByLottery.POWER_BALL !== null) && (
              <div className="flex gap-3 mt-2 text-[11px]" style={{ color: "var(--txt-3)" }}>
                <span>MM: {formatUsd(totalByLottery.MEGA_MILLIONS || 0)}</span>
                <span>·</span>
                <span>PB: {formatUsd(totalByLottery.POWER_BALL || 0)}</span>
              </div>
            )}
          </Card>
          <Card variant="soft">
            <div className="lg-label mb-1.5">Total spent</div>
            <div className="font-semibold" style={{ fontSize: 26, color: "var(--txt)" }}>
              {statsLoading ? "—" : formatUsd(totalSpent)}
            </div>
          </Card>
          <Card variant="soft">
            <div className="lg-label mb-1.5">Net result</div>
            <div className="font-semibold" style={{
              fontSize: 26,
              color: (totalWon ?? 0) - (totalSpent ?? 0) > 0 ? "var(--gold)" : "var(--txt-3)",
            }}>
              {(statsLoading || totalWon === null) ? "—" : formatUsd(totalWon - totalSpent)}
            </div>
          </Card>
          <Card variant="soft">
            <div className="lg-label mb-1.5">Picks this month</div>
            <div className="font-semibold" style={{ fontSize: 26, color: "var(--txt)" }}>
              {totalPicksThisMonth}
            </div>
          </Card>
        </div>

        <Card className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="lg-label">Upcoming draws</div>
            <Button size="sm" onClick={() => navigate("/generator")}>
              <Icon name="sparkles" size={14} />
              New pick
            </Button>
          </div>
          <UpcomingRow
            name="Mega Millions"
            schedule="Tue & Fri"
            jackpot={nextDraws.MEGA_MILLIONS?.value != null ? formatJackpot(nextDraws.MEGA_MILLIONS.value) : "$340M"}
            target={nextDraws.MEGA_MILLIONS?.target || NEXT_MEGA_MILLIONS()}
          />
          <UpcomingRow
            name="Powerball"
            schedule="Mon, Wed & Sat"
            jackpot={nextDraws.POWER_BALL?.value != null ? formatJackpot(nextDraws.POWER_BALL.value) : "$120M"}
            target={nextDraws.POWER_BALL?.target || NEXT_POWERBALL()}
            last
          />
        </Card>

        <div className="grid lg:grid-cols-2 gap-3 mb-4">
          <LotteryPicksCard
            title="Mega Millions"
            type={LOTTERY.MEGA_MILLIONS}
            picks={picks.MEGA_MILLIONS}
            totalWonOnLottery={totalByLottery.MEGA_MILLIONS}
            loading={loading}
            onViewAll={() => navigate("/picks?lottery=MEGA_MILLIONS")}
          />
          <LotteryPicksCard
            title="Powerball"
            type={LOTTERY.POWER_BALL}
            picks={picks.POWER_BALL}
            totalWonOnLottery={totalByLottery.POWER_BALL}
            loading={loading}
            onViewAll={() => navigate("/picks?lottery=POWER_BALL")}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-3">
          <BestResultCard result={bestResult} loading={statsLoading} />
          <HitDistributionCard rows={hitDistribution} loading={statsLoading} />
          <ExtraBallImpactCard rows={extraBallImpact} loading={statsLoading} />
        </div>
      </div>
    </AppShell>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */

function PickRow({ line, size = "sm" }) {
  if (!line) return null;
  const nums = lineToNumbers(line);
  const mainNums = nums.slice(0, 5);
  const special = nums[5];
  return (
    <BallsRow>
      {mainNums.map((n, i) => (
        <Ball key={`m-${i}`} value={n} size={size} />
      ))}
      {special !== null && special !== undefined && (
        <Ball value={special} size={size} variant="gold" />
      )}
    </BallsRow>
  );
}

function UpcomingRow({ name, schedule, jackpot, target, last = false }) {
  return (
    <div
      className="flex items-center justify-between py-3"
      style={{ borderBottom: last ? "none" : "1px solid var(--border)" }}
    >
      <div>
        <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
          {name}
        </div>
        <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
          {schedule}
        </div>
      </div>
      <div className="text-right">
        <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
          <Countdown target={target} compact />
        </div>
        <div className="text-[11px]" style={{ color: "var(--gold)" }}>
          {jackpot}
        </div>
      </div>
    </div>
  );
}

function LotteryPicksCard({
  title, type, picks, totalWonOnLottery, loading, onViewAll,
}) {
  return (
    <Card>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <div className="lg-label mb-0.5">{title}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>
            {picks.length} pick{picks.length === 1 ? "" : "s"} on file
            {totalWonOnLottery !== null && totalWonOnLottery !== undefined && (
              <>
                {" "}·{" "}
                <span style={{
                  color: totalWonOnLottery > 0 ? "var(--gold)" : "var(--txt-3)",
                  fontWeight: 500,
                }}>
                  Won {formatUsd(totalWonOnLottery)}
                </span>
              </>
            )}
          </div>
        </div>
        {picks.length > 0 && (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1 text-[12px] font-medium hover:underline whitespace-nowrap"
            style={{ color: "var(--gold)" }}
          >
            View all
            <Icon name="arrow-right" size={12} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-2">
          <Spinner size={14} />
          <span className="text-[12px]" style={{ color: "var(--txt-3)" }}>Loading…</span>
        </div>
      ) : picks.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="ticket-off" size={28} style={{ color: "var(--txt-3)" }} />
          <p className="text-[13px] mt-2" style={{ color: "var(--txt-2)" }}>
            No {title} picks yet.
          </p>
        </div>
      ) : (
        picks.slice(0, 4).map((game, idx) => (
          <PickCardRow
            key={`${game?.id ?? game?.buyDraw?.id ?? "k"}-${idx}`}
            game={game}
            type={type}
            isLast={idx >= Math.min(3, picks.length - 1)}
          />
        ))
      )}
    </Card>
  );
}

function PickCardRow({ game, type, isLast }) {
  const draw     = game?.buyDraw?.draw;
  const drawDate = draw?.date;
  return (
    <div
      className="py-3"
      style={{ borderBottom: isLast ? "none" : "1px solid var(--border)" }}
    >
      <PickRow line={{ ...game, _type: type }} size="sm" />
      <div className="flex items-center justify-between mt-2 gap-2">
        <span
          className="inline-flex items-center gap-1 text-[11px]"
          style={{ color: "var(--txt-2)" }}
        >
          <Icon name="calendar" size={11} />
          {formatDrawDate(drawDate)}
        </span>
        <StatusPill game={game} />
      </div>
    </div>
  );
}

/**
 * Single-pick status pill. Reads prize fields straight off the pick
 * (now overlaid server-side by LottoAnalyzerService). State machine:
 *
 *   prizeValueUsd == null   → "Drawing pending"
 *   winner === true         → "🎉 Jackpot! $X"
 *   prizeValueUsd > 0       → "Won $X"
 *   prizeValueUsd === 0     → "Not a winner"
 */
function StatusPill({ game }) {
  const prize = game?.prizeValueUsd;

  // No match result yet — drawing hasn't happened, or scheduler
  // hasn't created the row for this pick yet. Either way, pending
  // from the user's POV.
  if (prize === null || prize === undefined) {
    // Belt + suspenders: cross-check the draw's active flag /
    // date in case the backend overlays were skipped for some
    // reason. Both branches end up showing "Drawing pending"
    // so this is purely for clarity.
    const { pending } = getDrawStatus(game?.buyDraw?.draw);
    if (pending) {
      return (
        <span className="text-[11px] font-medium" style={{ color: "var(--txt-3)" }}>
          Drawing pending
        </span>
      );
    }
    // Drawing happened but no result yet — keep showing pending,
    // it'll resolve on the next page load.
    return (
      <span className="text-[11px] font-medium" style={{ color: "var(--txt-3)" }}>
        Drawing pending
      </span>
    );
  }

  if (game?.winner) {
    return (
      <span className="text-[11px] font-semibold" style={{ color: "var(--gold)" }}>
        🎉 Jackpot! {formatUsd(prize)}
      </span>
    );
  }
  if (prize > 0) {
    return (
      <span className="text-[11px] font-semibold" style={{ color: "var(--gold)" }}>
        Won {formatUsd(prize)}
      </span>
    );
  }
  return (
    <span className="text-[11px] font-medium" style={{ color: "var(--txt-3)" }}>
      Not a winner
    </span>
  );
}

/* ============================================================
   Personal performance cards
   ============================================================ */

function StatCardShell({ title, loading, empty, emptyIcon, emptyText, children }) {
  return (
    <Card>
      <div className="lg-label mb-3">{title}</div>
      {loading ? (
        <div className="flex items-center gap-2 py-2">
          <Spinner size={14} />
          <span className="text-[12px]" style={{ color: "var(--txt-3)" }}>Loading…</span>
        </div>
      ) : empty ? (
        <div className="text-center py-6">
          <Icon name={emptyIcon} size={28} style={{ color: "var(--txt-3)" }} />
          <p className="text-[13px] mt-2" style={{ color: "var(--txt-2)" }}>
            {emptyText}
          </p>
        </div>
      ) : children}
    </Card>
  );
}

/** "Your best result ever" — highest-prize line the user has won. */
function BestResultCard({ result, loading }) {
  return (
    <StatCardShell
      title="Your best result ever"
      loading={loading}
      empty={!result}
      emptyIcon="ticket-off"
      emptyText="No wins yet — keep playing!"
    >
      {result && (
        <>
          <BallsRow>
            {(result.generatedNumbers || []).map((n, i) => (
              <Ball key={`b-${i}`} value={n} size="sm" />
            ))}
            {result.generatedExtraBall !== null && result.generatedExtraBall !== undefined && (
              <Ball value={result.generatedExtraBall} size="sm" variant="gold" />
            )}
          </BallsRow>
          <div className="flex items-center justify-between mt-3">
            <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>
              {result.hits} number{result.hits === 1 ? "" : "s"} matched
              {result.extraMatched ? " + extra ball" : ""}
            </span>
            <span className="font-semibold text-[15px]" style={{ color: "var(--gold)" }}>
              {formatUsd(result.prizeValueUsd)}
            </span>
          </div>
        </>
      )}
    </StatCardShell>
  );
}

/** Bar-style breakdown of how many lines matched 0..5 numbers. */
function HitDistributionCard({ rows, loading }) {
  const maxLines = Math.max(1, ...rows.map((r) => r.totalLines || 0));
  return (
    <StatCardShell
      title="Hit distribution"
      loading={loading}
      empty={rows.length === 0}
      emptyIcon="chart-bar"
      emptyText="No lines scored yet."
    >
      <div className="space-y-2">
        {[...rows]
          .sort((a, b) => (a.hits ?? 0) - (b.hits ?? 0))
          .map((r) => (
            <div key={r.hits} className="flex items-center gap-2">
              <span className="text-[11px] w-14 flex-shrink-0" style={{ color: "var(--txt-2)" }}>
                {r.hits} hit{r.hits === 1 ? "" : "s"}
              </span>
              <div className="flex-1 h-[6px] rounded-full overflow-hidden" style={{ background: "var(--surf-2)" }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(4, ((r.totalLines || 0) / maxLines) * 100)}%`,
                    background: r.totalPrizeUsd > 0 ? "var(--gold)" : "var(--border-2)",
                  }}
                />
              </div>
              <span className="text-[11px] w-8 text-right flex-shrink-0" style={{ color: "var(--txt-3)" }}>
                {r.totalLines}
              </span>
            </div>
          ))}
      </div>
    </StatCardShell>
  );
}

/** Does buying the extra ball actually pay off for this user? */
function ExtraBallImpactCard({ rows, loading }) {
  const withExtra    = rows.find((r) => r.extraMatched === true);
  const withoutExtra = rows.find((r) => r.extraMatched === false);
  const row = (label, r, last = false) => {
    const lines = r?.totalLines || 0;
    const prize = r?.totalPrizeUsd || 0;
    const avg = lines > 0 ? prize / lines : 0;
    return (
      <div
        className="flex items-center justify-between py-2"
        style={{ borderBottom: last ? "none" : "1px solid var(--border)" }}
      >
        <div>
          <div className="text-[12px] font-medium" style={{ color: "var(--txt)" }}>{label}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>{lines} line{lines === 1 ? "" : "s"}</div>
        </div>
        <div className="text-right">
          <div className="text-[13px] font-semibold" style={{ color: prize > 0 ? "var(--gold)" : "var(--txt-3)" }}>
            {formatUsd(prize)}
          </div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>avg {formatUsd(avg)}/line</div>
        </div>
      </div>
    );
  };
  return (
    <StatCardShell
      title="Extra ball impact"
      loading={loading}
      empty={rows.length === 0}
      emptyIcon="ticket-off"
      emptyText="No lines scored yet."
    >
      {row("With extra ball", withExtra)}
      {row("Without extra ball", withoutExtra, true)}
    </StatCardShell>
  );
}
